import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Facebook, Globe, Instagram, Linkedin, Mail, MapPin, Menu, Phone, Twitter, X, Youtube } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCompanies, getEhiaaInitiative, getProjects, getSettings } from '../../lib/api';
import { BrandLogo, BrandMark, PhoneText, SectionTag } from '../common/BrandElements';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PublicLayout = () => {
  const { language, toggleLanguage, isRTL, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ehiaa, setEhiaa] = useState(null);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [companiesRes, projectsRes, settingsRes, ehiaaRes] = await Promise.all([
          getCompanies(),
          getProjects(),
          getSettings(),
          getEhiaaInitiative()
        ]);
        setCompanies(companiesRes.data.filter(c => c.is_active));
        setProjects(projectsRes.data.filter(p => p.is_active));
        setSettings(settingsRes.data);
        setEhiaa(ehiaaRes.data);
      } catch (error) {
        console.error('Error loading nav data:', error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: { ar: 'الرئيسية', en: 'Home' } },
    {
      path: '/companies',
      label: { ar: 'الشركات', en: 'Companies' },
      children: companies.map(c => ({ path: `/companies/${c.slug}`, label: { ar: c.name_ar, en: c.name_en } }))
    },
    {
      path: '/projects',
      label: { ar: 'المشاريع', en: 'Projects' },
      children: projects.map(p => ({ path: `/projects/${p.slug}`, label: { ar: p.title_ar, en: p.title_en } }))
    },
    ...(ehiaa?.is_active ? [{ path: '/ehiaa', label: { ar: ehiaa.title_ar || 'مبادرة إحياء', en: ehiaa.title_en || 'Ehiaa Initiative' } }] : []),
    { path: '/applications', label: { ar: 'تطبيقاتنا', en: 'Applications' } },
    { path: '/careers', label: { ar: 'انضم إلينا', en: 'Join Us' } },
    { path: '/board', label: { ar: 'مجلس الإدارة', en: 'Board' } },
    { path: '/news', label: { ar: 'أخبار الشركة', en: 'News' } },
    { path: '/contact', label: { ar: 'تواصل معنا', en: 'Contact' } },
  ];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const navLinkClass = (path) => `relative rounded-full px-4 py-2 text-center text-sm font-bold leading-tight transition-all ${isActive(path)
      ? 'bg-raad-teal text-white shadow-teal'
      : 'text-raad-charcoal/75 hover:bg-raad-teal/10 hover:text-raad-charcoal'
    }`;

  const socialLinks = [
    { url: settings?.facebook_url, icon: Facebook, label: 'Facebook' },
    { url: settings?.twitter_url, icon: Twitter, label: 'X' },
    { url: settings?.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: settings?.linkedin_url, icon: Linkedin, label: 'LinkedIn' },
    { url: settings?.youtube_url, icon: Youtube, label: 'YouTube' },
    { url: settings?.whatsapp_number ? `https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}` : '', icon: Phone, label: 'WhatsApp' },
  ].filter(link => link.url);

  return (
    <div className="min-h-screen bg-raad-porcelain text-raad-charcoal" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3">
        <div className={`mx-auto max-w-[1520px] rounded-[1.65rem] border transition-all duration-300 ${isScrolled || mobileMenuOpen
            ? 'border-white/80 bg-white/90 py-3 shadow-soft backdrop-blur-2xl'
            : 'border-white/55 bg-white/65 py-4 backdrop-blur-xl'
          }`}>
          <div className="container mx-auto flex items-center justify-between gap-4 px-4 sm:px-6">
            <Link to="/" className="flex items-center rounded-2xl px-2 py-1 transition-transform hover:scale-[1.01]" aria-label="Raad Holding home">
              <BrandLogo settings={settings} className="h-12 sm:h-14" />
            </Link>

            <nav className="hidden xl:flex items-center gap-1">
              {navItems.map((item) => (
                item.children && item.children.length > 0 ? (
                  <DropdownMenu key={item.path}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className={`${navLinkClass(item.path)} h-auto min-h-9 max-w-28 whitespace-normal gap-1`}>
                        {t(item.label.ar, item.label.en)}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="min-w-56 rounded-2xl border-raad-teal/15 bg-white p-2 shadow-soft">
                      <DropdownMenuItem asChild>
                        <Link to={item.path} className="cursor-pointer rounded-xl px-3 py-2 font-semibold text-raad-charcoal hover:bg-raad-teal/10">
                          {t('عرض الكل', 'View All')}
                        </Link>
                      </DropdownMenuItem>
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.path} asChild>
                          <Link to={child.path} className="cursor-pointer rounded-xl px-3 py-2 text-raad-charcoal/70 hover:bg-raad-teal/10 hover:text-raad-charcoal">
                            {t(child.label.ar, child.label.en)}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link key={item.path} to={item.path} className={`${navLinkClass(item.path)} flex min-h-9 max-w-28 items-center justify-center whitespace-normal`}>
                    {t(item.label.ar, item.label.en)}
                  </Link>
                )
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="rounded-full border border-raad-teal/20 bg-white/70 text-raad-charcoal hover:bg-raad-teal hover:text-white"
              >
                <Globe className="w-4 h-4 me-2" />
                {language === 'ar' ? 'EN' : 'عربي'}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="xl:hidden rounded-full text-raad-charcoal hover:bg-raad-teal/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </Button>
            </div>
          </div>

          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="xl:hidden overflow-hidden"
              >
                <nav className="container mx-auto grid gap-2 px-6 pb-4 pt-3 sm:grid-cols-2">
                  {navItems.map((item) => (
                    <div key={item.path}>
                      <Link to={item.path} className={`${navLinkClass(item.path)} flex min-h-12 items-center justify-center rounded-2xl whitespace-normal`}>
                        {t(item.label.ar, item.label.en)}
                      </Link>
                      {item.children && item.children.length > 0 && (
                        <div className="mt-1 space-y-1 ps-3">
                          {item.children.slice(0, 5).map((child) => (
                            <Link key={child.path} to={child.path} className="block rounded-xl px-3 py-2 text-sm text-raad-charcoal/60 hover:bg-raad-teal/10 hover:text-raad-charcoal">
                              {t(child.label.ar, child.label.en)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="relative overflow-hidden border-t border-raad-teal/15 bg-white">
        <div className="brand-grid absolute inset-x-0 top-0 h-52 opacity-40" />
        <div className="absolute end-0 top-0 h-8 w-96 max-w-[70vw] bg-raad-teal/20 brand-slash-end" />
        <div className="container relative z-10 mx-auto px-6 py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.25fr_0.75fr_0.9fr_1fr]">
            <div>
              <BrandLogo settings={settings} className="h-16" />
              <p className="mt-5 max-w-md text-sm leading-7 text-raad-graphite/70">
                {t(
                  'رعد القابضة تجمع بين الثبات المؤسسي والرؤية الرقمية الحديثة من دمشق إلى قطاعات متعددة.',
                  'Raad Holding blends institutional strength with a modern digital outlook from Damascus across diverse sectors.'
                )}
              </p>
              <div className="mt-6 flex items-center gap-3">
                <BrandMark className="h-9 w-9" />
                <SectionTag className="tracking-[0.22em]">SYRIA</SectionTag>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-base font-black text-raad-charcoal">{t('روابط سريعة', 'Quick Links')}</h3>
              <ul className="space-y-3">
                {navItems.slice(0, 6).map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} className="text-sm font-semibold text-raad-graphite/65 hover:text-raad-teal">
                      {t(item.label.ar, item.label.en)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-4 text-base font-black text-raad-charcoal">{t('شركاتنا', 'Our Companies')}</h3>
              <ul className="space-y-3">
                {companies.slice(0, 6).map((company) => (
                  <li key={company.id}>
                    <Link to={`/companies/${company.slug}`} className="text-sm font-semibold text-raad-graphite/65 hover:text-raad-teal">
                      {t(company.name_ar, company.name_en)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-4 text-base font-black text-raad-charcoal">{t('تواصل معنا', 'Contact Us')}</h3>
              <ul className="space-y-4 text-sm font-semibold text-raad-graphite/70">
                <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-raad-teal" />
                  <span>{settings ? t(settings.address_ar, settings.address_en) : t('دمشق، سوريا', 'Damascus, Syria')}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-raad-teal" />
                  <span>{settings?.contact_email || 'info@raad-ho.com'}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-raad-teal" />
                  <PhoneText>{settings?.contact_phone || '+963 932 888 111'}</PhoneText>
                </li>
              </ul>

              {socialLinks.length > 0 && (
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {socialLinks.map(({ url, icon: Icon, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-raad-teal/20 bg-raad-porcelain text-raad-charcoal transition-colors hover:bg-raad-teal hover:text-white"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-raad-teal/15 pt-8 text-sm text-raad-graphite/55 md:flex-row">
            <p>© {new Date().getFullYear()} {t('رعد القابضة. جميع الحقوق محفوظة', 'Raad Holding. All Rights Reserved')}</p>
            <p className="font-bold tracking-[0.25em] text-raad-teal">www.raad-ho.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
